/**
 * Component header chính của ứng dụng
 * 
 * HINTS:
 * - Có thể thêm haptic feedback khi tap
 * - Có thể thêm pull-to-refresh
 * - Có thể thêm swipe actions
 * - Có thể thêm notification badge
 * - Có thể thêm dark mode
 * - Có thể tối ưu re-render với React.memo()
 */

import React, { useEffect, useState, useRef } from 'react'
import { HeaderProps, HeaderTheme } from './types'
import { IconButton } from './buttons/IconButton'
import { BackIcon } from './icons/BackIcon'
import { PangoLogo } from './icons/PangoLogo'
import { useStaff } from '@/contexts/StaffContext'
import { 
  PlusIcon, 
  QrCodeIcon, 
  LinkIcon, 
  UserPlusIcon, 
  QueueListIcon, 
  ShoppingBagIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  HomeIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  UserIcon
} from '@heroicons/react/24/outline'
import { QRCodePopup } from './QRCodePopup'
import { InviteLinkPopup } from './InviteLinkPopup'
import { QRCodeScannerPopup } from './QRCodeScannerPopup'
import { CustomerFormPopup } from './CustomerFormPopup'
import { OrderFormPopup } from './OrderFormPopup'
import { usePopup } from '@/contexts/PopupContext'

const SCREEN_ICONS: Record<string, React.FC<{ className?: string }>> = {
  'home': HomeIcon,
  'messages': ChatBubbleLeftRightIcon,
  'customers': UserGroupIcon,
  'tasks': ClipboardDocumentListIcon,
  'settings': Cog6ToothIcon,
  'công việc': Squares2X2Icon,
  'tin nhắn': ChatBubbleLeftRightIcon,
  'khách hàng': UserGroupIcon,
  'tài khoản': UserIcon
}

const getThemeStyles = (theme: HeaderTheme = 'primary') => {
  const baseStyles = 'transition-all duration-300'
  
  switch (theme) {
    case 'secondary':
      return `${baseStyles} bg-indigo-600`
    case 'transparent':
      return `${baseStyles} bg-transparent backdrop-blur-sm`
    default:
      return `${baseStyles} bg-blue-600`
  }
}

interface HeaderLogoProps {
  className?: string
  onClick?: () => void
}

const HeaderLogo: React.FC<HeaderLogoProps> = ({
  className = '',
  onClick
}) => {
  const { staffInfo } = useStaff()
  return (
    <div 
      className={`flex items-center cursor-default ${className}`}
      onClick={onClick}
    >
      <PangoLogo className="w-6 h-6 text-white" />
      <div className="ml-2">
        <div className="flex items-center gap-2">
          <span className="text-lg line-clamp-1">Chào, {staffInfo.fullName || staffInfo.nickname}</span>
        </div>
      </div>
    </div>
  )
}

interface HeaderTitleProps {
  title?: string
  subtitle?: string
  showLogo?: boolean
  currentScreen?: string
}

const HeaderTitle: React.FC<HeaderTitleProps> = ({
  subtitle,
  currentScreen
}) => {
  const { staffInfo } = useStaff()
  const screenKey = currentScreen?.toLowerCase() || ''
  const IconComponent = SCREEN_ICONS[screenKey]
  
  return (
    <div className="flex items-center">
      <div className="flex items-center gap-3">
        {IconComponent && (
          <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
            <IconComponent className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <div className="text-md font-medium text-white leading-tight truncate max-w-[200px] line-clamp-1">
            Chào, {staffInfo.fullName || staffInfo.nickname}!
          </div>
        </div>
      </div>
    </div>
  )
}

export const Header: React.FC<HeaderProps> = ({
  shouldHideUI,
  onLogoClick,
  pageTitle,
  pageSubtitle,
  showBackButton,
  onBackClick,
  theme = 'primary',
  isSticky = false,
  currentScreen
}) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [showActionMenu, setShowActionMenu] = useState(false)
  const [showQRCode, setShowQRCode] = useState(false)
  const [showInviteLink, setShowInviteLink] = useState(false)
  const [showQRScanner, setShowQRScanner] = useState(false)
  const [showCustomerForm, setShowCustomerForm] = useState(false)
  const [showOrderForm, setShowOrderForm] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { openPopup, closePopup } = usePopup()
  const prevShowActionMenu = React.useRef(showActionMenu)

  useEffect(() => {
    if (!isSticky) return

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isSticky])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowActionMenu(false)
      }
    }

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowActionMenu(false)
      }
    }

    if (showActionMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [showActionMenu])

  const handleActionClick = () => {
    setShowActionMenu(!showActionMenu)
  }

  const actionItems = [
    {
      icon: <QrCodeIcon className="w-5 h-5" />,
      label: 'Tạo mã QR',
      onClick: () => {
        setShowQRCode(true)
        setShowActionMenu(false)
      }
    },
    {
      icon: <LinkIcon className="w-5 h-5" />,
      label: 'Tạo Link mới',
      onClick: () => {
        setShowInviteLink(true)
        setShowActionMenu(false)
      }
    },
    {
      icon: <UserPlusIcon className="w-5 h-5" />,
      label: 'Thêm khách',
      onClick: () => {
        setShowCustomerForm(true)
        setShowActionMenu(false)
      }
    },
    {
      icon: <ShoppingBagIcon className="w-5 h-5" />,
      label: 'Tạo đơn hàng',
      onClick: () => {
        setShowOrderForm(true)
        setShowActionMenu(false)
      }
    },
    {
      icon: <QueueListIcon className="w-5 h-5" />,
      label: 'Quét mã QR',
      onClick: () => {
        setShowQRScanner(true)
        setShowActionMenu(false)
      }
    }
  ]

  useEffect(() => {
    if (!prevShowActionMenu.current && showActionMenu) {
      openPopup()
    }
    if (prevShowActionMenu.current && !showActionMenu) {
      closePopup()
    }
    prevShowActionMenu.current = showActionMenu
  }, [showActionMenu])

  if (shouldHideUI) return null

  const showLogo = !pageTitle && !currentScreen

  const headerClasses = [
    getThemeStyles(theme),
    isSticky && 'sticky top-0 z-50',
    isScrolled && 'shadow-lg'
  ].filter(Boolean).join(' ')

  return (
    <>
      <div className={headerClasses}>
        <header className="text-white top-0 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center min-w-0">
            {showBackButton && (
              <IconButton 
                onClick={onBackClick}
                className="mr-3 flex-shrink-0"
              >
                <BackIcon />
              </IconButton>
            )}
            {showLogo ? (
              <HeaderLogo 
                onClick={onLogoClick}
              />
            ) : (
              <HeaderTitle
                title={currentScreen || pageTitle}
                subtitle={pageSubtitle}
                showLogo={!showBackButton}
                currentScreen={currentScreen}
              />
            )}
          </div>

          {/* Action Button */}
          <div className="relative">
            <button
              ref={buttonRef}
              onClick={handleActionClick}
              className={`
                w-7 h-7
                flex items-center justify-center 
                rounded-full
                bg-transparent
                border border-white/50
                hover:bg-white/30 
                active:bg-white/40
                active:scale-95
                transition-all duration-200
                shadow-lg
                ${showActionMenu ? 'bg-white/30' : ''}
              `}
            >
              <PlusIcon 
                className={`
                  w-4 h-4 text-white
                  transition-transform duration-200 ease-in-out
                  ${showActionMenu ? 'rotate-45' : ''}
                `}
              />
            </button>

            {/* Action Menu */}
            {showActionMenu && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black/50 z-40"
                  onClick={() => setShowActionMenu(false)}
                />
                
                {/* Menu */}
                <div 
                  ref={menuRef}
                  className={`
                    absolute right-0 mt-2 w-56
                    bg-white rounded-2xl
                    py-2
                    z-50 
                    transform origin-top-right
                    transition-all duration-200 ease-out
                    ring-1 ring-black/5
                  `}
                >
                  {actionItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.onClick()
                        setShowActionMenu(false)
                      }}
                      className={`
                        w-full px-4 py-3
                        text-left text-gray-700
                        hover:bg-gray-50
                        active:bg-gray-100
                        flex items-center gap-3
                        transition-colors duration-150
                        group
                        ${index === 0 ? 'rounded-t-2xl' : ''}
                        ${index === actionItems.length - 1 ? 'rounded-b-2xl' : ''}
                      `}
                    >
                      <span className="p-2 rounded-xl bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-900 transition-colors duration-150">
                        {item.icon}
                      </span>
                      <span className="font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>
      </div>
      
      <QRCodePopup 
        isOpen={showQRCode}
        onClose={() => setShowQRCode(false)}
      />

      <InviteLinkPopup
        isOpen={showInviteLink}
        onClose={() => setShowInviteLink(false)}
      />

      <QRCodeScannerPopup
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
      />

      <CustomerFormPopup
        isOpen={showCustomerForm}
        onClose={() => setShowCustomerForm(false)}
      />

      <OrderFormPopup
        isOpen={showOrderForm}
        onClose={() => setShowOrderForm(false)}
      />
    </>
  )
}